import { Link } from "react-router-dom";
import { ShieldOff, ArrowLeft, Sparkles } from "lucide-react";

/**
 * NotIncludedInPlan (2026-07-06)
 *
 * Clean fallback screen for modules / features / workflows that the
 * tenant's subscription does not include. Rendered by RequireEntitlement
 * when a direct route is accessed without the matching module/feature/
 * workflow bit set on the snapshot.
 *
 * Never crash. Never surface the raw backend 403 JSON — that goes to
 * the logs; the human-facing screen stays calm and actionable.
 */
export default function NotIncludedInPlan({
  moduleKey,
  featureKey,
  workflowKey,
  title = "This module is not included in your current plan.",
  message,
  backTo = "/",
  backLabel = "Back to Dashboard",
}) {
  const label = moduleKey
    ? `Module: ${prettify(moduleKey)}`
    : featureKey
    ? `Feature: ${prettify(featureKey)}`
    : workflowKey
    ? `Workflow: ${prettify(workflowKey)}`
    : null;
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-16" data-testid="not-included-in-plan">
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 mb-5">
          <ShieldOff className="w-6 h-6 text-slate-500" />
        </div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100" data-testid="not-included-title">{title}</h1>
        {label ? (
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-full px-2.5 py-1">
            <Sparkles className="w-3 h-3" /> {label}
          </div>
        ) : null}
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-4">
          {message || "Contact your administrator to upgrade your subscription and unlock this area."}
        </p>
        <Link to={backTo}
              className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm font-semibold"
              data-testid="not-included-back">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </Link>
      </div>
    </div>
  );
}

function prettify(k) {
  return String(k || "")
    .replace(/[_.]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
